import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { JsonConvert } from 'json2typescript';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { LookupModel } from 'infrastructure-shared';
import { PerIdentificationSubtypeQueryModel } from '../model/per-identification-subtype-query-model';
import { HrConfigurationService } from '../../../common/service/hr.configuration.service';
import { PerIdentificationSubtypeModel } from '../model/per-identification-subtype-model';

@Injectable({
    providedIn: 'root',
})
export class PerIdentificationSubtypeService {
    endpoint: string;
    readonly serviceRootPattern = '/perIdentificationSubtypeService/identificationSubtype/';

    constructor(private httpClient: HttpClient, private configService: HrConfigurationService) {
        this.endpoint = configService.config.hr.personnel + '';
    }

    public queryListByLocale(
        perIdentificationSubtypeQueryModel: PerIdentificationSubtypeQueryModel,
        locale: string,
    ): Observable<Array<PerIdentificationSubtypeModel>> {
        const pattern = this.serviceRootPattern + 'query/byLocale/' + locale;
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<PerIdentificationSubtypeModel>>>(
                this.endpoint + pattern,
                converter.serialize(perIdentificationSubtypeQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], PerIdentificationSubtypeModel);
                }),
            );
    }

    public save(
        perIdentificationSubtypeModel: PerIdentificationSubtypeModel,
    ): Observable<PerIdentificationSubtypeModel> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<PerIdentificationSubtypeModel>>(
                this.endpoint + pattern,
                converter.serialize(perIdentificationSubtypeModel, PerIdentificationSubtypeModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerIdentificationSubtypeModel);
                }),
            );
    }

    public queryPage(
        pagination: PerIdentificationSubtypeQueryModel,
    ): Observable<HvlPage<Array<PerIdentificationSubtypeModel>>> {
        const pattern = this.serviceRootPattern + 'page';
        return this.httpClient
            .post<HvlResponse<HvlPage<PerIdentificationSubtypeModel[]>>>(this.endpoint + pattern, pagination)
            .pipe(
                map((hvlpage) => {
                    const converter = new JsonConvert();
                    if (hvlpage.body?.totalElements === 0) {
                        return new HvlPage<Array<PerIdentificationSubtypeModel>>(
                            [],
                            hvlpage.body.totalElements,
                            hvlpage.body.totalPages,
                            hvlpage.errorDetail,
                        );
                    }
                    const model = converter.deserializeArray(hvlpage.body.data, PerIdentificationSubtypeModel);
                    return new HvlPage<Array<PerIdentificationSubtypeModel>>(
                        model,
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }),
            );
    }

    public queryList(
        perIdentificationSubtypeQueryModel: PerIdentificationSubtypeQueryModel,
    ): Observable<Array<PerIdentificationSubtypeModel>> {
        const pattern = this.serviceRootPattern + 'query';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<PerIdentificationSubtypeModel>>>(
                this.endpoint + pattern,
                converter.serialize(perIdentificationSubtypeQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], PerIdentificationSubtypeModel);
                }),
            );
    }

    public lookup(): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookup';
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.get<HvlResponse<Array<LookupModel>>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], LookupModel);
            }),
        );
    }

    public update(
        perIdentificationSubtypeModel: PerIdentificationSubtypeModel,
    ): Observable<PerIdentificationSubtypeModel> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();
        return this.httpClient
            .put<HvlResponse<PerIdentificationSubtypeModel>>(
                this.endpoint + pattern,
                converter.serialize(perIdentificationSubtypeModel, PerIdentificationSubtypeModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerIdentificationSubtypeModel);
                }),
            );
    }

    public getList(): Observable<Array<PerIdentificationSubtypeModel>> {
        const pattern = this.serviceRootPattern;
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.get<HvlResponse<Array<PerIdentificationSubtypeModel>>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], PerIdentificationSubtypeModel);
            }),
        );
    }

    public getById(id: number): Observable<PerIdentificationSubtypeModel> {
        const pattern = this.serviceRootPattern + id;
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.get<HvlResponse<PerIdentificationSubtypeModel>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerIdentificationSubtypeModel);
            }),
        );
    }

    public deleteById(id: number): Observable<any> {
        const pattern = this.serviceRootPattern + id;
        return this.httpClient.delete(this.endpoint + pattern);
    }

    public lookupQuery(queryModel: PerIdentificationSubtypeQueryModel): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookup/query';
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.post<HvlResponse<Array<LookupModel>>>(serviceUrl, queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], LookupModel);
            }),
        );
    }
}
